/**
 * AUDIO_PROVIDER.JSX - Provider du système audio complet
 * 
 * Ce provider initialise et gère le système audio de l'application
 * avec Howler.js. Il précharge tous les sons, gère le volume/mute,
 * et fournit le contexte audio aux composants enfants.
 * 
 * RESPONSABILITÉS:
 * - Initialiser Howler.js avec tous les sons
 * - Gérer le volume principal
 * - Gérer le mute (manuel et automatique)
 * - Précharger tous les sons au montage
 * - Fournir le contexte audio
 * - Écouter les raccourcis clavier (M pour mute)
 * - Mute automatique en arrière-plan (visibilitychange)
 * 
 * OPTIMISATIONS:
 * - Préchargement avec Howler.js
 * - Double application du volume (RAF workaround)
 * - Cleanup complet au démontage
 * - Synchronisation avec Zustand store
 */

import { createContext, useContext, useEffect } from 'react'
import { Howl, Howler } from 'howler'
import { useAudioStore } from '../store/useAudioStore'
import { SOUNDS_CONFIG } from '../config/sounds'

/**
 * Context pour le système audio
 * Fourni par AudioProvider, consommé par useSounds et hooks dérivés
 */
export const AudioContext = createContext(null)

/**
 * Hook pour accéder au contexte audio
 * 
 * @returns {object} Contexte audio { sounds, play, ... }
 */
export function useAudioContext() {
  const context = useContext(AudioContext)
  
  if (!context) {
    throw new Error('useAudioContext doit être utilisé dans un AudioProvider')
  }
  
  return context
}

/**
 * AudioProvider Component
 * 
 * Initialise le système audio et fournit le contexte aux enfants.
 * 
 * @param {object} props
 * @param {React.ReactNode} props.children - Composants enfants
 * @returns {JSX.Element}
 */
export function AudioProvider({ children }) {
  const { masterVolume, muted, setSounds } = useAudioStore()

  // ============================================
  // INITIALISATION DES SONS
  // ============================================

  useEffect(() => {

    // Objet pour stocker les sons chargés
    const loadedSounds = {}

    // Charger chaque son défini dans SOUNDS_CONFIG
    Object.entries(SOUNDS_CONFIG).forEach(([name, config]) => {

      // Créer une instance Howl pour chaque fichier
      const instances = config.files.map((file) => {
        return new Howl({
          src: [file],
          preload: true,        // Préchargement
          volume: 1,            // Volume par défaut (géré par masterVolume)
          html5: false,         // Utiliser Web Audio API (meilleure qualité)
          loop: config.loop || false  // Boucle si définie dans la config
        })
      })

      // Stocker dans l'objet
      loadedSounds[name] = {
        instances,              // Instances Howl
        lastTime: 0,            // Timestamp dernière lecture
        config                  // Configuration depuis SOUNDS_CONFIG
      }
    })

    // Mettre à jour le store Zustand
    setSounds(loadedSounds)

    // ============================================
    // CLEANUP AU DÉMONTAGE
    // ============================================

    return () => {      
      // Arrêter et décharger tous les sons
      Object.values(loadedSounds).forEach(({ instances }) => {
        instances.forEach((howl) => {
          howl.unload()
        })
      })
    }
  }, [setSounds])

  // ============================================
  // GESTION DU VOLUME PRINCIPAL
  // ============================================

  useEffect(() => {
    // Application du volume via Howler.js
    Howler.volume(masterVolume)

    // Double application dans RAF (workaround Howler.js)
    // Assure que le volume est bien pris en compte
    window.requestAnimationFrame(() => {
      Howler.volume(masterVolume)
    })
  }, [masterVolume])

  // ============================================
  // GESTION DU MUTE
  // ============================================

  useEffect(() => {
    // Application du mute via Howler.js
    Howler.mute(muted)

  }, [muted])

  // ============================================
  // RACCOURCI CLAVIER 'M' POUR MUTE
  // ============================================

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Touche M ou m
      if (event.key === 'm' || event.key === 'M') {
        // Éviter si focus dans input/textarea
        if (
          event.target.tagName === 'INPUT' ||
          event.target.tagName === 'TEXTAREA'
        ) {
          return
        }

        // Toggle mute
        useAudioStore.getState().toggleMute()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  // ============================================
  // MUTE AUTOMATIQUE EN ARRIÈRE-PLAN
  // ============================================

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page en arrière-plan → mute automatique
        Howler.mute(true)
        console.log('🔇 Page en arrière-plan: mute automatique')
      } else {
        // Page visible → restaurer état mute
        const currentMuted = useAudioStore.getState().muted
        Howler.mute(currentMuted)
        console.log(`🔊 Page visible: mute ${currentMuted ? 'ON' : 'OFF'}`)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // ============================================
  // VALEUR DU CONTEXTE
  // ============================================

  // Pour l'instant, le contexte est simple
  // Les sons sont dans le store Zustand (accessible via useAudioStore)
  const contextValue = {
    ready: true
  }

  // ============================================
  // RENDER
  // ============================================

  return (
    <AudioContext.Provider value={contextValue}>
      {children}
    </AudioContext.Provider>
  )
}

/**
 * NOTES D'IMPLÉMENTATION:
 * 
 * 1. PRÉCHARGEMENT:
 *    Tous les sons sont préchargés au montage avec preload: true.
 *    Howler.js gère le chargement asynchrone en arrière-plan.
 * 
 * 2. VOLUME/MUTE:
 *    - Volume et mute sont dans le store Zustand
 *    - AudioProvider écoute les changements et applique via Howler.js
 *    - Double application du volume (RAF) pour fiabilité
 * 
 * 3. MUTE AUTOMATIQUE:
 *    - visibilitychange API détecte passage arrière-plan
 *    - Mute automatique pour économiser ressources
 *    - Restaure état utilisateur au retour
 * 
 * 4. RACCOURCI CLAVIER:
 *    - Touche M pour toggle mute
 *    - Évite déclenchement dans inputs
 *    - Global dans toute l'application
 * 
 * 5. CLEANUP:
 *    - Décharge tous les sons au démontage
 *    - Libère mémoire
 *    - Évite fuites mémoire
 * 
 * 6. REACT STRICTMODE:
 *    - Le composant se monte/démonte/remonte en dev (StrictMode)
 *    - Les sons sont rechargés à chaque montage
 *    - Cleanup correct empêche les fuites mémoire
 *    - En production, monte une seule fois
 */

